import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Store, KeyRound, Search, Loader2, Hourglass, ShieldCheck, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { startImpersonation, useCountdown, fmtClock, STATUS_LABELS, END_REASONS, MESSAGE_KEY } from '../lib/firmAccess';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DURATIONS = [5, 15, 30];
const REQUEST_TTL_MS = 10 * 60 * 1000;
const errMsg = (e) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'İşlem başarısız');

const enter = async (s) => {
  try {
    const r = await axios.post(`${API}/firm-access/sessions/${s.id}/enter`);
    startImpersonation(r.data.access_token);
  } catch (e) { toast.error(errMsg(e)); }
};

const PendingCard = ({ s, onCancel }) => {
  const left = useCountdown(new Date(new Date(s.requested_at).getTime() + REQUEST_TTL_MS).toISOString());
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="firm-access-pending-card">
      <Hourglass className="h-5 w-5 text-amber-600 animate-pulse" />
      <span className="flex-1 min-w-[220px]"><b>{s.company_name}</b> firmasının onayı bekleniyor ({s.minutes} dk). Yanıtlanmazsa talep <b className="font-mono">{fmtClock(left)}</b> sonra düşer.</span>
      <Button size="sm" variant="outline" onClick={onCancel} data-testid="firm-access-cancel-btn">Talebi İptal Et</Button>
    </div>
  );
};

const StatusArea = ({ current, onCancel }) => {
  if (!current) return null;
  if (current.status === 'pending') return <PendingCard s={current} onCancel={onCancel} />;
  if (current.status === 'active') {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" data-testid="firm-access-active-card">
        <ShieldCheck className="h-5 w-5 text-emerald-600" />
        <span className="flex-1"><b>{current.company_name}</b> erişiminizi onayladı.</span>
        <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => enter(current)} data-testid="firm-access-enter-btn">Firma Ekranına Geç</Button>
      </div>
    );
  }
  const st = STATUS_LABELS[current.status] || {};
  return (
    <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="firm-access-last-result">
      <Info className="h-4 w-4" /> Son talep: <b>{current.company_name}</b> — <span className={`rounded px-2 py-0.5 text-xs ${st.color}`}>{st.label}</span>
      {current.end_reason && <span className="text-xs">({END_REASONS[current.end_reason]})</span>}
    </div>
  );
};

export default function FirmAccessPage() {
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [minutes, setMinutes] = useState(15);
  const [current, setCurrent] = useState(null);
  const [sending, setSending] = useState(false);
  const prevStatus = useRef(null);

  useEffect(() => {
    const msg = sessionStorage.getItem(MESSAGE_KEY);
    if (msg) { sessionStorage.removeItem(MESSAGE_KEY); toast.info(msg); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => axios.get(`${API}/firm-access/companies`, { params: { q } }).then(r => setCompanies(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const loadMine = () => axios.get(`${API}/firm-access/mine`).then(r => {
    const s = r.data;
    if (prevStatus.current === 'pending' && s?.status === 'active') { toast.success(`${s.company_name} erişimi onayladı, firma ekranına geçiliyor`); enter(s); }
    if (prevStatus.current === 'pending' && s?.status === 'rejected') toast.error(`${s.company_name} erişim talebini reddetti`);
    if (prevStatus.current === 'pending' && s?.status === 'request_expired') toast.warning('Talep 10 dakika içinde yanıtlanmadığı için düştü');
    prevStatus.current = s?.status || null;
    setCurrent(s);
  }).catch(() => {});

  // Firma yanıtı anlık bildirimle gelir ('firm-access'); 30 sn yedek kontrol + sekmeye dönüş
  useEffect(() => {
    loadMine();
    const t = setInterval(() => { if (!document.hidden) loadMine(); }, 30000);
    const onVisible = () => { if (!document.hidden) loadMine(); };
    window.addEventListener('firm-access', loadMine);
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); window.removeEventListener('firm-access', loadMine); document.removeEventListener('visibilitychange', onVisible); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Bekleyen talebin 10 dakikası dolduğu anda durum tazelenir
  useEffect(() => {
    if (current?.status !== 'pending' || !current?.requested_at) return undefined;
    const t = setTimeout(loadMine, Math.max(0, new Date(current.requested_at).getTime() + 10 * 60000 - Date.now()) + 1000);
    return () => clearTimeout(t);
  }, [current?.status, current?.requested_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const busy = ['pending', 'active'].includes(current?.status);
  const shown = typeFilter === 'all' ? companies : companies.filter(c => c.company_type === typeFilter);

  const send = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/firm-access/requests`, { company_id: selected.id, minutes });
      toast.success('Erişim talebi firmaya gönderildi');
      prevStatus.current = 'pending';
      loadMine();
    } catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };

  const cancel = async () => {
    try { await axios.post(`${API}/firm-access/requests/${current.id}/cancel`); prevStatus.current = null; loadMine(); toast.info('Talep iptal edildi'); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (user?.role !== 'isg_expert' || !user?.firm_access_enabled) {
    return <p className="text-slate-500" data-testid="firm-access-no-permission">Bu sayfayı görüntüleme yetkiniz yok.</p>;
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="firm-access-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2"><KeyRound className="h-7 w-7 text-red-600" /> Firma Erişimi</h1>
        <p className="text-sm text-slate-500 mt-1">Firmayı seçin, süre belirleyin. Firma onaylarsa firma portalını firma adına kullanabilirsiniz; tüm erişimler kayıt altına alınır.</p>
      </div>
      <StatusArea current={current} onCancel={cancel} />
      <Card>
        <CardHeader><CardTitle className="text-lg">Yeni Erişim Talebi</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Firma ara..." className="pl-9" data-testid="firm-access-search" />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="firm-access-type-filter">
            {[['all', 'Tümü'], ['firma', 'Firmalar'], ['magaza', 'Mağazalar']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setTypeFilter(k)}
                className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${typeFilter === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'}`}
                data-testid={`firm-access-type-${k}`}>
                {l} <span className="opacity-60">{k === 'all' ? companies.length : companies.filter(c => c.company_type === k).length}</span>
              </button>
            ))}
          </div>
          <div className="max-h-96 overflow-y-auto rounded-lg border border-slate-200 divide-y" data-testid="firm-access-company-list">
            {shown.length === 0 && <p className="p-4 text-sm text-slate-400">Kayıt bulunamadı.</p>}
            {shown.map(c => {
              const Icon = c.company_type === 'magaza' ? Store : Building2;
              return (
                <button key={c.id} type="button" disabled={!c.can_request} onClick={() => setSelected(c)} title={c.reason || undefined}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors disabled:cursor-not-allowed ${selected?.id === c.id ? 'bg-red-50 text-red-800 font-medium' : c.can_request ? 'hover:bg-slate-50' : 'opacity-60'}`}
                  data-testid={`firm-access-company-${c.id}`}>
                  <Icon className={`h-4 w-4 shrink-0 ${c.company_type === 'magaza' ? 'text-blue-500' : 'text-slate-400'}`} />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{c.name}</span>
                    {c.reason && <span className="block truncate text-[11px] text-slate-500">{c.reason}</span>}
                  </span>
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${c.company_type === 'magaza' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{c.company_type === 'magaza' ? 'Mağaza' : 'Firma'}</span>
                  {selected?.id === c.id && <span className="text-xs">Seçildi</span>}
                </button>
              );
            })}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Erişim süresi</p>
            <div className="flex gap-2">
              {DURATIONS.map(d => (
                <button key={d} type="button" onClick={() => setMinutes(d)}
                  className={`rounded-full px-5 py-2 text-sm font-semibold border transition-colors ${minutes === d ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}
                  data-testid={`firm-access-duration-${d}`}>{d} dk</button>
              ))}
            </div>
          </div>
          <Button onClick={send} disabled={!selected || busy || sending} className="bg-red-600 hover:bg-red-700" data-testid="firm-access-send-btn">
            {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {selected ? `${selected.name} firmasına ${minutes} dk erişim talebi gönder` : 'Önce firma seçin'}
          </Button>
          {busy && <p className="text-xs text-slate-500">Bekleyen veya aktif bir erişiminiz varken yeni talep gönderemezsiniz.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
